import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from './api';

export type Product = {
  id: number;
  lineCode: string;
  lineName: string;
  partNumber: string;
  description: string;
  uom: string;
  packQty: number;
  listPrice: string;
  showPrice: string | null;
  imageUrl: string | null;
  isFeatured: boolean;
};

export type CatalogueLine = { id: number; code: string; name: string; productCount: number };

export type Sort = 'pertinence' | 'part' | 'description' | 'prix';

export type Filters = {
  q: string;
  lineCode: string | null;
  featuredOnly: boolean;
  sort: Sort;
};

const PAGE_SIZE = 40;

type Page = { products: Product[]; total: number; offset: number; hasMore: boolean };

/**
 * Catalogue paginé. On charge par pages plutôt que tout d'un coup : le fichier
 * fournisseur fait déjà 408 produits, et le réseau d'une salle de congrès ne
 * pardonne pas les grosses réponses.
 */
export function useCatalogue(filters: Filters) {
  const params = (offset: number) => {
    const p = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(offset),
      sort: filters.sort,
    });
    if (filters.q.trim()) p.set('q', filters.q.trim());
    if (filters.lineCode) p.set('lineCode', filters.lineCode);
    if (filters.featuredOnly) p.set('featured', 'true');
    return p.toString();
  };

  const query = useInfiniteQuery<Page>({
    queryKey: ['catalogue', filters],
    queryFn: ({ pageParam }) => api<Page>(`/api/products?${params(pageParam as number)}`),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.offset + last.products.length : undefined),
  });

  return {
    products: query.data?.pages.flatMap((p) => p.products) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error as Error | undefined,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

export function useCatalogueLines() {
  return useQuery<{ lines: CatalogueLine[] }>({
    queryKey: ['catalogue', 'lines'],
    queryFn: () => api('/api/products/lines'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Diffère la valeur de recherche : on ne veut pas une requête par frappe sur un
 * clavier tactile. 250 ms est assez court pour paraître instantané.
 */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Prix réellement facturé : le prix show s'il existe, sinon le prix de liste. */
export function effectivePrice(p: Product): string {
  return p.showPrice ?? p.listPrice;
}

/** Pourcentage de rabais arrondi, ou null s'il n'y a pas de rabais à montrer. */
export function discountPercent(p: Product): number | null {
  if (!p.showPrice) return null;
  const list = Number(p.listPrice);
  const show = Number(p.showPrice);
  if (!(list > 0) || show >= list) return null;
  return Math.round((1 - show / list) * 100);
}
